import { toFarsiNumberStrict } from "@utils/digits";
import { DOMNode, Element } from "html-react-parser";

const PERSIAN_REGEX = /[\u0600-\u06FF]/;

export const processKatexOutput = (
  root: HTMLElement,
  convertDigits: boolean = false
) => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);

  const textNodes: Text[] = [];

  while (walker.nextNode()) {
    textNodes.push(walker.currentNode as Text);
  }

  textNodes.forEach((textNode) => {
    const value = textNode.nodeValue ?? "";

    if (!value.trim()) return;

    const fragment = document.createDocumentFragment();

    const parts = value.split(/(\d+|[\u0600-\u06FF]+)/g);

    let changed = false;

    parts.forEach((part) => {
      if (!part) return;

      const isPersian = PERSIAN_REGEX.test(part);
      const isDigits = /^\d+$/.test(part);

      if (isPersian) {
        const span = document.createElement("span");
        span.className = "katex-fa-text";
        span.textContent = part;
        fragment.appendChild(span);
        changed = true;
        return;
      }

      if (convertDigits && isDigits) {
        const span = document.createElement("span");
        span.className = "katex-fa-digit";
        span.textContent = toFarsiNumberStrict(part);
        fragment.appendChild(span);
        changed = true;
        return;
      }

      fragment.appendChild(document.createTextNode(part));
    });

    if (changed) {
      textNode.parentNode?.replaceChild(fragment, textNode);
    }
  });
};

export const processMathTags = (encodedHtml: string) => {
  let decodedHtml = encodedHtml.replace(
    /&lt;\/math([\s\S]*?)&gt;/gi,
    function (_match, attributes) {
      const decodedAttributes = attributes
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'");
      return `<math${decodedAttributes}>`;
    }
  );

  decodedHtml = decodedHtml.replace(/&lt;math&gt;/gi, "</math>");

  decodedHtml = decodedHtml.replace(
    /<p>\s*(<math[\s\S]*?<\/math>)\s*<\/p>/gi,
    "$1"
  );

  return decodedHtml;
};

export const getMathTextContent = (children: DOMNode[]): string => {
  let text = "";
  for (const child of children) {
    if (child.type === "text" && "data" in child) text += child?.data;
    else if ((child as Element).children)
      text += getMathTextContent((child as Element).children);
  }
  return text;
};

export const parseMathTexContent = (
  content: string
): { math: string; displayMode: boolean } => {
  const trimmed = content.trim();

  const patterns: Array<{ regex: RegExp; displayMode: boolean }> = [
    { regex: /^\\\[([\s\S]*?)\\\]$/, displayMode: true },
    { regex: /^\\\(([\s\S]*?)\\\)$/, displayMode: false },
    { regex: /^\$\$([\s\S]*?)\$\$$/, displayMode: true },
    { regex: /^\$([\s\S]*?)\$$/, displayMode: false },
  ];

  for (const { regex, displayMode } of patterns) {
    const match = trimmed.match(regex);
    if (match) {
      return { math: match[1].trim(), displayMode };
    }
  }

  return { math: trimmed, displayMode: false };
};
